import {
  classifyLookup,
  classifyManage,
  licenceForJurisdiction,
  refusedRequestMessage,
  FOUND,
  NOT_ENROLLED,
  UNAVAILABLE,
} from '@/api/nursysApi';

/**
 * The public /verifyNurse submit: look her licence up, enrol her with NCSBN
 * only when Nursys says she is not on the list, look her up again, and hand a
 * licence in good standing to the page's finalize.
 *
 * It lives outside VerifyNursePage.vue so every branch can be run without
 * mounting the page; the page supplies the requests, the dialog and the loader.
 *
 * THE LOADER ALWAYS CLOSES AND SHE IS ALWAYS TOLD SOMETHING. The chain this
 * replaced had no rejection arm, and axios rejects every 401, 403, 429 and 5xx,
 * so a refused lookup left the spinner up for good and said nothing. On dev a
 * refreshed page had lost its invite, keyon's gate answered 401
 * unauthenticated, and that spinner is what she saw.
 *
 * Only NOT_ENROLLED may lead to the enrolment, because it sends the last four
 * of her SSN and her birth year to the regulator's service; see
 * api/nursysApi.js classifyLookup for why an outage must never look like it.
 */

/** A 401, or keyon's invite_required: the invite did not arrive, or is spent or expired. */
export const INVITE_UNUSABLE =
  'This verification link can no longer be used in this window. Please open the link from your invitation email again, or ask for a new invitation.';
export const UNREACHABLE_NOT_SUBMITTED =
  'We could not reach the license verification service just now. Your details have not been submitted. Please try again in a few minutes.';
export const UNREACHABLE_NOT_SAVED =
  'We could not reach the license verification service just now. Your details have not been saved. Please try again in a few minutes.';
export const UNREACHABLE =
  'We could not reach the license verification service just now. Please try again in a few minutes.';

const UNEXPECTED = 'Unable to verify this license right now. Please try again.';

/** Nursys LicenseStatus values that refuse the account, upper-cased. */
const REFUSED_STATUSES = ['ENCUMBERED', 'EXPIRED', 'SUSPENSION', 'SUSPENSION (SEE HISTORY)'];

/**
 * Is this rejection about the invite? Null when it is not.
 *
 * The page has no session, so a 401 can only mean the invite header did not
 * arrive - a reload used to lose it - and invite_required means keyon read the
 * token and it is spent, expired, or the account is no longer pending. Both
 * get invite wording rather than keyon's "sign in again", which asks a nurse
 * who has no account yet to do something she cannot. Only invite_required
 * says the token itself is dead, so only it tells the page to drop it.
 *
 * @param {*} error an axios rejection, or anything thrown
 * @returns {{text: string, inviteSpent: boolean}|null}
 */
export function inviteRefusal(error) {
  const res = error && error.response;
  const data = res && res.data && typeof res.data === 'object' ? res.data : {};
  if (data.code === 'invite_required') {
    return { text: INVITE_UNUSABLE, inviteSpent: true };
  }
  if ((res && Number(res.status) === 401) || data.code === 'unauthenticated') {
    return { text: INVITE_UNUSABLE, inviteSpent: false };
  }

  return null;
}

/**
 * What a rejected lookup or enrolment means on this page: the invite wording,
 * keyon's own sentence for any other refusal, or `unreachable` for no answer,
 * a rate limit or a 5xx (api/nursysApi.js refusedRequestMessage).
 *
 * @param {*} error an axios rejection, or anything thrown
 * @param {string} unreachable
 * @returns {{text: string, inviteSpent: boolean}}
 */
export function verifyNurseRequestFailure(error, unreachable) {
  return inviteRefusal(error) || { text: refusedRequestMessage(error) || unreachable, inviteSpent: false };
}

/** `fn`, run at most once. */
function once(fn) {
  let done = false;
  return () => {
    if (done) return;
    done = true;
    if (typeof fn === 'function') fn();
  };
}

/**
 * @param {{form: object, ltype: string}} facts the page's form and 'RN' | 'LPN'
 * @param {object} deps
 * @param {function(object): Promise} deps.lookup posts LookupNursesInfo; resolves to the axios response
 * @param {function(object): Promise} deps.add posts Add_Nurse; resolves to the axios response
 * @param {function(object, {hide: function}): Promise} deps.complete the page's finalize, given the
 *        licence row and a loader it may close; it tells her the outcome itself
 * @param {function({type: string, title: string, text: string})} deps.notify the page's dialog
 * @param {function()} deps.hide closes the loader
 * @param {function()} deps.inviteSpent drops the invite token
 */
export async function runVerifyNurseFlow(facts, deps) {
  const hide = once(deps.hide);
  const tell = (type, text) => {
    hide();
    deps.notify({ type, title: 'Error', text });
  };

  let licence = null;
  try {
    licence = await decide(facts, deps, tell);
  } catch (err) {
    // An answer shaped unlike anything above - a licence row without a status
    // used to throw inside the old then-callback. Never read as a good licence.
    tell('warning', UNEXPECTED);
  }

  try {
    if (licence) {
      // The finalize keeps the loader up while it runs and closes it itself.
      await deps.complete(licence, { hide });
    }
  } finally {
    hide();
  }
}

/** Resolves to the licence row to finalize, or null once she has been told why not. */
async function decide({ form, ltype }, deps, tell) {
  const lookupRequest = { rnlicense: form.license, jurisdiction: form.licenseSt, ltype };

  const refused = (error, unreachable) => {
    const failure = verifyNurseRequestFailure(error, unreachable);
    if (failure.inviteSpent && typeof deps.inviteSpent === 'function') {
      deps.inviteSpent();
    }
    tell('warning', failure.text);
    return null;
  };

  let first;
  try {
    first = await deps.lookup(lookupRequest);
  } catch (error) {
    return refused(error, UNREACHABLE_NOT_SUBMITTED);
  }

  const verdict = classifyLookup(first && first.data);
  if (verdict.kind === UNAVAILABLE) {
    tell('warning', UNREACHABLE_NOT_SUBMITTED);
    return null;
  }
  if (verdict.kind !== FOUND && verdict.kind !== NOT_ENROLLED) {
    tell('warning', verdict.message ||
      'That license could not be verified. Please check the license number and state and try again.');
    return null;
  }
  if (verdict.kind === FOUND) {
    return inGoodStanding(verdict.licences, form.licenseSt, tell);
  }

  // Her own address, which the form has had all along. This used to file
  // "Manhattan / New York / 10001" and a made-up practice setting with NCSBN
  // under her name, for every nurse who registered.
  //
  // notficationenabled / reminderenabled subscribe her to mail from the
  // regulator's service. No screen asks, so they are off: eight of the eleven
  // call sites sent "Y" by copy-paste from one block.
  let added;
  try {
    added = await deps.add({
      jurisdiction: form.licenseSt,
      state: form.state,
      licensnum: form.license,
      bdayear: form.dob.substring(0, 4),
      lastfourssn: form.ssn,
      email: form.email,
      address1: form.addr,
      city: form.city,
      zip: form.zip,
      hospitalpracsetting: '',
      notficationenabled: 'N',
      reminderenabled: 'N',
      ltype,
    });
  } catch (error) {
    return refused(error, UNREACHABLE_NOT_SAVED);
  }

  const enrolment = classifyManage(added && added.data);
  if (enrolment.kind !== FOUND) {
    // Do not blame her details for our outage. keyon answers "No Server
    // Response" far more often than Nursys actually rejects anything.
    tell('warning', enrolment.kind === UNAVAILABLE
      ? UNREACHABLE_NOT_SAVED
      : enrolment.message ||
        'The license could not be verified. Please make sure the information is correct, particularly your date of birth and the last 4 of your SSN.');
    return null;
  }

  let second;
  try {
    second = await deps.lookup(lookupRequest);
  } catch (error) {
    return refused(error, UNREACHABLE);
  }

  const verdict2 = classifyLookup(second && second.data);
  if (verdict2.kind !== FOUND) {
    tell('warning', verdict2.kind === UNAVAILABLE
      ? UNREACHABLE
      : 'Unable to verify this license right now. Please check the data and try again.');
    return null;
  }

  return inGoodStanding(verdict2.licences, form.licenseSt, tell);
}

/** The row for her state when it may be finalized, or null once she has been told why not. */
function inGoodStanding(licences, jurisdiction, tell) {
  const row = licenceForJurisdiction(licences, jurisdiction);
  if (!row) {
    tell('warning', 'The license state cannot be verified. Please make sure the information is correct.');
    return null;
  }

  // Throws on a row without a status, which runVerifyNurseFlow reports; it is
  // never taken as good standing.
  const status = row.LicenseStatus.toUpperCase();
  if (REFUSED_STATUSES.includes(status)) {
    tell('error', `This license number in the state of '${jurisdiction}' is '${status.toLowerCase()}'. The account cannot be verified.`);
    return null;
  }

  return row;
}
